// Types for the onboarding flow

export interface OnboardingData {
  // Step 2: Career Profile
  industry: string;
  experienceLevel: 'Junior' | 'Mid-Level' | 'Senior' | 'Executive';
  currentJobTitle?: string;
  
  // Step 3: Goals and Preferences
  careerGoals: string[];
  preferredJobTitles: string[];
  targetCompanies?: string[];
  salaryRange?: string;
  workPreference: 'remote' | 'hybrid' | 'onsite' | 'flexible';
  howHeardAboutUs: string;
  
  // Step 4: Additional preferences
  jobSearchUrgency: 'immediate' | 'within_month' | 'within_3_months' | 'exploring';
  primaryMotivation: string;
  additionalNotes?: string;
  
  // Persona-specific data
  teamSize?: string;
  leadershipExperience?: boolean;
  techSpecialization?: string[];
  skillGaps?: string[];
  educationFocus?: string;
}

// User persona types for dynamic onboarding
export type UserPersona = 
  | 'tech_senior'
  | 'tech_junior' 
  | 'executive'
  | 'career_changer'
  | 'returner'
  | 'new_graduate'
  | 'professional';

export interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  component: React.ComponentType<OnboardingStepProps>;
  isOptional?: boolean;
  estimatedTime?: string;
}

export interface OnboardingStepProps {
  data: Partial<OnboardingData>;
  onDataChange: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStep: number;
  totalSteps: number;
  isLoading?: boolean;
  userPersona?: UserPersona | null;
  questionType?: string;
}

export interface OnboardingState {
  currentStep: number;
  data: Partial<OnboardingData>;
  isLoading: boolean;
  error: string | null;
  isComplete: boolean;
}

// Industry options with categories
export const INDUSTRY_OPTIONS = [
  {
    category: 'Technology',
    options: [
      'Software Development',
      'Data Science & Analytics',
      'Cybersecurity',
      'DevOps & Cloud',
      'Product Management',
      'UX/UI Design',
      'IT Support & Administration',
      'Artificial Intelligence & Machine Learning',
      'Mobile Development',
      'Web Development'
    ]
  },
  {
    category: 'Business & Finance',
    options: [
      'Finance & Banking',
      'Accounting',
      'Investment & Trading',
      'Insurance',
      'Real Estate',
      'Business Analysis',
      'Management Consulting',
      'Project Management',
      'Operations',
      'Supply Chain & Logistics'
    ]
  },
  {
    category: 'Healthcare & Life Sciences',
    options: [
      'Healthcare Administration',
      'Nursing',
      'Medical Practice',
      'Pharmaceuticals',
      'Biotechnology',
      'Medical Devices',
      'Clinical Research',
      'Public Health',
      'Mental Health',
      'Veterinary'
    ]
  },
  {
    category: 'Marketing & Sales',
    options: [
      'Digital Marketing',
      'Content Marketing',
      'Social Media Marketing',
      'Sales',
      'Business Development',
      'Customer Success',
      'Public Relations',
      'Brand Management',
      'Market Research',
      'E-commerce'
    ]
  },
  {
    category: 'Education & Training',
    options: [
      'K-12 Education',
      'Higher Education',
      'Corporate Training',
      'Educational Technology',
      'Curriculum Development',
      'Academic Research',
      'Student Services',
      'Educational Administration',
      'Online Learning',
      'Special Education'
    ]
  },
  {
    category: 'Creative & Media',
    options: [
      'Graphic Design',
      'Video Production',
      'Photography',
      'Writing & Editing',
      'Advertising',
      'Entertainment',
      'Publishing',
      'Broadcasting',
      'Gaming',
      'Fashion'
    ]
  },
  {
    category: 'Other',
    options: [
      'Government',
      'Non-Profit',
      'Legal',
      'Manufacturing',
      'Construction',
      'Transportation',
      'Hospitality',
      'Retail',
      'Agriculture',
      'Energy & Utilities'
    ]
  }
];

// Career goals options
export const CAREER_GOALS_OPTIONS = [
  'Land a new job in my field',
  'Switch to a different industry',
  'Get promoted in current company',
  'Increase salary significantly',
  'Find remote work opportunities',
  'Transition to leadership role',
  'Start freelancing/consulting',
  'Break into tech industry',
  'Work for a specific company',
  'Improve work-life balance',
  'Gain new skills and experience',
  'Return to workforce after break'
];

// How heard about us options
export const HOW_HEARD_OPTIONS = [
  'Google Search',
  'Social Media (LinkedIn, Twitter, etc.)',
  'Friend or Colleague Referral',
  'Online Advertisement',
  'Blog or Article',
  'YouTube or Video Content',
  'Job Search Website',
  'Career Coach Recommendation',
  'University/Career Center',
  'Professional Network',
  'App Store',
  'Other'
];

// Primary motivation options
export const MOTIVATION_OPTIONS = [
  'Not getting enough interview calls',
  'Want to optimize for specific companies',
  'Need help with ATS compatibility',
  'Looking to improve resume quality',
  'Want to save time in job applications',
  'Seeking professional resume feedback',
  'Preparing for career transition',
  'Want to stand out from competition'
];

// Work preference options
export const WORK_PREFERENCE_OPTIONS = [
  { value: 'remote', label: 'Remote Only', description: 'Work from anywhere' },
  { value: 'hybrid', label: 'Hybrid', description: 'Mix of remote and office' },
  { value: 'onsite', label: 'On-site', description: 'Office-based work' },
  { value: 'flexible', label: 'Flexible', description: 'Open to any arrangement' }
];

// Job search urgency options
export const URGENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediate', description: 'Actively applying now' },
  { value: 'within_month', label: 'Within a Month', description: 'Planning to start soon' },
  { value: 'within_3_months', label: 'Within 3 Months', description: 'Preparing for future search' },
  { value: 'exploring', label: 'Just Exploring', description: 'Casually looking at options' }
];

// Salary range options
export const SALARY_RANGE_OPTIONS = [
  'Under $50,000',
  '$50,000 - $75,000',
  '$75,000 - $100,000',
  '$100,000 - $150,000',
  '$150,000 - $200,000',
  '$200,000 - $300,000',
  'Over $300,000',
  'Prefer not to say'
];
